import { lazy } from 'react'
import { Navigate, Route } from 'react-router-dom'
import { Gate } from './shell/Gate'

const Home = lazy(() => import('@/routes/Home'))
const TovOS = lazy(() => import('@/routes/TovOS'))
const Hoxton = lazy(() => import('@/routes/Hoxton'))
const Agenda = lazy(() => import('@/routes/Agenda'))
const Plans = lazy(() => import('@/routes/Plans'))
const PlanView = lazy(() => import('@/routes/plans/PlanView'))
const Mail = lazy(() => import('@/routes/Mail'))
const Projects = lazy(() => import('@/routes/Projects'))
const ProjectDetail = lazy(() => import('@/routes/ProjectDetail'))
const Status = lazy(() => import('@/routes/Status'))
const Office = lazy(() => import('@/routes/Office'))
const OfficeFloorPage = lazy(() => import('@/routes/office/OfficeFloorPage'))
const StatusDetail = lazy(() => import('@/routes/StatusDetail'))
const Workshop = lazy(() => import('@/routes/Workshop'))
const Preview = lazy(() => import('@/routes/Preview'))
const Audits = lazy(() => import('@/routes/Audits'))
const Design = lazy(() => import('@/routes/Design'))
const PdfDocs = lazy(() => import('@/routes/PdfDocs'))
const Studio = lazy(() => import('@/routes/Studio'))
const Datasets = lazy(() => import('@/routes/workshop/Datasets'))
const DatasetDetail = lazy(() => import('@/routes/workshop/DatasetDetail'))
const DatasetNew = lazy(() => import('@/routes/workshop/DatasetNew'))
const Loras = lazy(() => import('@/routes/workshop/Loras'))
const LoraDetail = lazy(() => import('@/routes/workshop/LoraDetail'))
const LoraNew = lazy(() => import('@/routes/workshop/LoraNew'))
const Playground = lazy(() => import('@/routes/workshop/Playground'))
const StudioList = lazy(() => import('@/routes/workshop/Studio'))
const StudioNew = lazy(() => import('@/routes/workshop/StudioNew'))
const StudioDetail = lazy(() => import('@/routes/workshop/StudioDetail'))
const Channels = lazy(() => import('@/routes/workshop/Channels'))
const ChannelEditor = lazy(() => import('@/routes/workshop/ChannelEditor'))
const Transcripts = lazy(() => import('@/routes/workshop/Transcripts'))
const TranscriptDetail = lazy(() => import('@/routes/workshop/TranscriptDetail'))

/**
 * Shared route children — V1's route table verbatim, every element
 * behind the migration Gate (R22). The floor mounts these under the
 * AppShellV2 layout route; each workspace window mounts the same set
 * under its own window-scoped MemoryRouter (one-place rule, R8), so
 * window content never reads the floor's location.
 *
 * Consolidation (feat/subnav-office-tovos): the AI surfaces collapse to
 * two — Office (Floor/Studio/Design/Audits nested behind a fancy SubNav)
 * and TovOS (Archie + eight sibling views incl. herdr/Workshop/Plans).
 * Collab and Hermes are retired; their old paths redirect to the survivors.
 */
export function surfaceRoutes() {
  return (
    <>
      <Route index element={<Gate surface="home" el={<Home />} />} />
      {/* TovOS — single plain gated route; cockpit views switch in-page
          (TovOS.tsx `view` state via the shared SubNav), so no nested router
          is introduced (landmine #3). */}
      <Route path="tovos" element={<Gate surface="tovos" el={<TovOS />} />} />
      {/* Hoxton — 24/7 planner/PA (ZeroClaw daemon) + zijn agenda. Rides the
          tovos gate surface: same audience, no separate migration flag. */}
      <Route path="hoxton" element={<Gate surface="tovos" el={<Hoxton />} />} />
      <Route path="agenda" element={<Gate surface="tovos" el={<Agenda />} />} />
      <Route path="projects">
        <Route index element={<Gate surface="projects" el={<Projects />} />} />
        <Route path=":slug" element={<Gate surface="projects" el={<ProjectDetail />} />} />
      </Route>
      {/* U8: one mail surface — V1's Inbox merges into Mail (AE5). Deep
          /inbox/:id links land on the merged surface (the n8n thread id
          space doesn't map to IMAP uids); the n8n pipeline itself stays
          alive untouched for V1 until V1 deletion. */}
      <Route path="inbox" element={<Navigate to="/mail" replace />} />
      <Route path="inbox/:id" element={<Navigate to="/mail" replace />} />
      <Route path="status">
        <Route index element={<Gate surface="status" el={<Status />} />} />
        <Route path=":service" element={<Gate surface="status" el={<StatusDetail />} />} />
      </Route>
      {/* Office — nested-route shell (mirrors the retired Hermes shell). Floor
          is the index; Studio/Design/Audits fold in as child tabs. The whole
          subtree stays inside AppShellV2 (operator-only); office.tovie.it.com
          mounts OfficeGuest via the App.tsx host branch, so these never leak. */}
      <Route path="office" element={<Gate surface="office" el={<Office />} />}>
        <Route index element={<OfficeFloorPage />} />
        <Route path="studio" element={<Studio />} />
        <Route path="design" element={<Design />} />
        <Route path="pdfcn" element={<PdfDocs />} />
        <Route path="audits" element={<Audits />} />
      </Route>
      {/* Retired standalone surfaces now live inside Office — keep old deep
          links alive by redirecting to their new nested homes. */}
      <Route path="studio" element={<Navigate to="/office/studio" replace />} />
      <Route path="design" element={<Navigate to="/office/design" replace />} />
      <Route path="audits" element={<Navigate to="/office/audits" replace />} />
      {/* herdr fleet folded into the TovOS Agents tab; Collab + Hermes retired
          — all redirect to TovOS (its Agents/Archie views own that ground). */}
      <Route path="agents" element={<Navigate to="/tovos" replace />} />
      <Route path="collab" element={<Navigate to="/tovos" replace />} />
      <Route path="collab/*" element={<Navigate to="/tovos" replace />} />
      <Route path="hermes" element={<Navigate to="/tovos" replace />} />
      <Route path="hermes/*" element={<Navigate to="/tovos" replace />} />
      {/* Plans + Workshop stay top-level routes (their deep children keep their
          own URLs); they're just removed from the sidebar — TovOS surfaces
          their index views as tabs. */}
      <Route path="plans">
        <Route index element={<Gate surface="plans" el={<Plans />} />} />
        <Route path=":id" element={<Gate surface="plans" el={<PlanView />} />} />
      </Route>
      <Route path="mail" element={<Gate surface="mail" el={<Mail />} />} />
      <Route path="preview" element={<Gate surface="preview" el={<Preview />} />} />
      <Route path="pdfcn" element={<PdfDocs />} />
      <Route path="workshop">
        <Route index element={<Gate surface="workshop" el={<Workshop />} />} />
        <Route path="datasets" element={<Gate surface="workshop" el={<Datasets />} />} />
        <Route path="datasets/new" element={<Gate surface="workshop" el={<DatasetNew />} />} />
        <Route path="datasets/:id" element={<Gate surface="workshop" el={<DatasetDetail />} />} />
        <Route path="loras" element={<Gate surface="workshop" el={<Loras />} />} />
        <Route path="loras/new" element={<Gate surface="workshop" el={<LoraNew />} />} />
        <Route path="loras/:id" element={<Gate surface="workshop" el={<LoraDetail />} />} />
        <Route path="playground" element={<Gate surface="workshop" el={<Playground />} />} />
        <Route path="studio" element={<Gate surface="workshop" el={<StudioList />} />} />
        <Route path="studio/new" element={<Gate surface="workshop" el={<StudioNew />} />} />
        <Route path="studio/:id" element={<Gate surface="workshop" el={<StudioDetail />} />} />
        <Route path="channels" element={<Gate surface="workshop" el={<Channels />} />} />
        <Route path="channels/:slug" element={<Gate surface="workshop" el={<ChannelEditor />} />} />
        <Route path="transcripts" element={<Gate surface="workshop" el={<Transcripts />} />} />
        <Route path="transcripts/:id" element={<Gate surface="workshop" el={<TranscriptDetail />} />} />
      </Route>
    </>
  )
}
